import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CaretDown, SpinnerGap, Warning } from '@phosphor-icons/react';
import {
    getPrincipleLibraryConfig,
    getPrincipleLibraryPending,
    getPrincipleLibraryAccepted,
    getPrincipleLibraryRejected,
    submitPrincipleFeedback,
} from '@/lib/api';
import { INTENT_TO_BUTTON } from '@/config/task-display-config';
import {
    DESIGN_INTENTS,
    DS_BORDERS,
    DS_COLORS,
    DS_SPACING,
    DS_TYPOGRAPHY,
} from '@/config/ui-invariants';
import { toast } from 'sonner';

/**
 * Principle Library Agent — see-and-retire lens-review surface.
 * Authority: WI-PRINCIPLE-LIBRARY-GOVERNANCE-BUILD-001
 * Route: /agents/principle-library
 *
 * Three tabs (pending/accepted/rejected).
 * Actions: Agree, Override (reason modal), Promote to capping, Park, Unpark.
 * GTC Link-1: AGT-052 trigger_ui_surface.handler_name="handleFeedback"
 *              in component PrincipleLibraryAgent.jsx.
 * All user-visible copy from GET /api/principle-library/config — never hardcoded.
 */

// ─── Tabs ──────────────────────────────────────────────────────────────────────
// Labels overridden from config.tab_labels once loaded; these are fallbacks only
// during the initial load flash (no copy is rendered until config resolves).
const TAB_IDS = ['pending', 'accepted', 'rejected'];

// ─── Enforcement mode badge intent ────────────────────────────────────────────
function enforcementModeIntent(mode) {
    if (mode === 'capping') return DESIGN_INTENTS.positive;
    return DESIGN_INTENTS.info; // 'observe'
}

// ─── Admission-checks panel (WI-2 / Contract §11.6) ──────────────────────────────
// Five pass/fail chips + the worked-example before→after block. All copy from
// config.qualifier_labels (never hardcoded). Accessibility: every chip carries a TEXT
// verdict (Met / Not met / Not provided) alongside the colour — never colour alone.
function QualifierChip({ label, state, labels }) {
    // state: 'pass' | 'fail' | 'unknown'
    const intent = state === 'pass' ? DESIGN_INTENTS.positive
        : state === 'fail' ? DESIGN_INTENTS.negative
            : DESIGN_INTENTS.neutral;
    const verdict = state === 'pass' ? (labels.pass || 'Met')
        : state === 'fail' ? (labels.fail || 'Not met')
            : (labels.not_set || 'Not provided');
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${intent.bg} ${intent.text} ${intent.border}`}
            style={{ fontSize: '13px', fontWeight: 700, minHeight: '28px' }}
        >
            {label} · {verdict}
        </span>
    );
}

function QualifierPanel({ principle, labels }) {
    const qf = principle.qualifier_flags || null;
    const we = principle.worked_example || null;
    const decks = Array.isArray(principle.source_deck_refs) ? principle.source_deck_refs : [];
    const deckCount = qf ? (qf.source_deck_refs_count || 0) : decks.length;
    const deckFloor = (qf && qf.source_deck_refs_floor) || 2;

    // Grandfathered seeds carry no qualifier_flags → 'unknown' (Not provided), except
    // falsifiable, which we can read straight off the lens.
    const st = (v) => (v === true ? 'pass' : v === false ? 'fail' : 'unknown');
    const rules = [
        { key: 'changes', text: labels.rule_changes_answer || 'Changed a real call', state: qf ? st(qf.worked_example_present) : (we ? 'pass' : 'unknown') },
        { key: 'distinct', text: labels.rule_distinct || 'Distinct lens', state: qf ? st(qf.trigger_situation_set) : (principle.trigger_situation ? 'pass' : 'unknown') },
        { key: 'falsifiable', text: labels.rule_falsifiable || 'Has a disproof', state: principle.falsifying_condition ? 'pass' : 'fail' },
        { key: 'recurs', text: labels.rule_recurs || 'Seen across decks', state: qf ? (deckCount >= deckFloor ? 'pass' : 'fail') : (deckCount >= deckFloor ? 'pass' : 'unknown') },
        { key: 'vocab', text: labels.rule_fits_vocab || 'Recognised method', state: qf ? st(qf.move_in_vocab) : (principle.analytical_move ? 'pass' : 'unknown') },
    ];

    const sectionLabelStyle = {
        fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
        fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
        fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
        textTransform: 'uppercase', letterSpacing: '0.1em',
        color: DS_COLORS.text.brand, marginBottom: '6px',
    };

    return (
        <div style={{ marginBottom: DS_SPACING.paragraphSpacing + 'px' }}>
            <div style={sectionLabelStyle}>{labels.section_heading || 'Admission checks'}</div>
            <div className="flex items-center gap-2 flex-wrap">
                {rules.map(r => (
                    <QualifierChip key={r.key} label={r.text} state={r.state} labels={labels} />
                ))}
            </div>

            {we && (
                <div
                    className="mt-3 p-3 rounded"
                    style={{
                        background: DS_COLORS.surface.warm,
                        border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                        borderLeft: `4px solid ${DS_COLORS.border.brand}`,
                    }}
                >
                    <div style={sectionLabelStyle}>{labels.worked_example_heading || 'What this lens changed'}</div>
                    <div className="flex gap-4 flex-wrap" style={{ marginBottom: '8px' }}>
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: DS_COLORS.text.secondary }}>{labels.before_band || 'Initial read'}</div>
                            <div style={{ fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily, fontSize: DS_TYPOGRAPHY.bodyMd.fontSize, color: DS_COLORS.text.primary }}>{we.before_band || '—'}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: DS_COLORS.text.secondary }}>{labels.after_band || 'After applying the lens'}</div>
                            <div style={{ fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily, fontSize: DS_TYPOGRAPHY.bodyMd.fontSize, color: DS_COLORS.text.primary }}>{we.after_band || '—'}</div>
                        </div>
                    </div>
                    {we.rationale && (
                        <p style={{ fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily, fontSize: DS_TYPOGRAPHY.bodyMd.fontSize, lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight, color: DS_COLORS.text.primary, margin: 0 }}>
                            {we.rationale}
                        </p>
                    )}
                </div>
            )}

            {decks.length > 0 && (
                <p style={{ fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily, fontSize: '14px', color: DS_COLORS.text.secondary, marginTop: '8px', marginBottom: 0 }}>
                    {labels.source_decks || 'Decks it was seen on'}: {decks.join(', ')}
                </p>
            )}
        </div>
    );
}

// ─── Modal selector (override reason / park confirm) ──────────────────────────
function ModalSelector({ open, title, options, onSelect, onClose, busy }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: DS_COLORS.overlay.modal }}
            onClick={onClose}
        >
            <div
                className="rounded-lg p-6"
                style={{
                    background: DS_COLORS.surface.white,
                    border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                    boxShadow: DS_COLORS.shadow.card,
                    minWidth: '420px',
                    maxWidth: '600px',
                }}
                onClick={e => e.stopPropagation()}
            >
                <h3
                    className="mb-4"
                    style={{
                        fontFamily: DS_TYPOGRAPHY.listItemTitle.fontFamily,
                        fontSize: DS_TYPOGRAPHY.listItemTitle.fontSize,
                        fontWeight: DS_TYPOGRAPHY.listItemTitle.fontWeight,
                        color: DS_COLORS.text.primary,
                    }}
                >
                    {title}
                </h3>
                <div className="flex flex-col gap-2">
                    {options.map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => onSelect(opt.value)}
                            disabled={busy}
                            className={`text-left px-4 py-3 rounded-lg border transition-colors ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} disabled:opacity-50`}
                            style={{
                                minHeight: DS_SPACING.minTouchTarget + 'px',
                                fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <div className="flex justify-end mt-4">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className={`px-4 py-2 rounded-lg border ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border}`}
                        style={{
                            minHeight: DS_SPACING.minTouchTarget + 'px',
                            fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                            fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                            fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Watcher flags evidence list ───────────────────────────────────────────────
// `label` comes from config.column_labels.watcher_flags. The heading used to be the
// hardcoded words "Watcher Flags" — the name of an internal component (the principle
// governance watcher) shown to the CEO as though it were a thing he knows about.
function WatcherFlags({ flags, label }) {
    if (!flags || flags.length === 0) return null;
    return (
        <div
            className="mt-3 p-3 rounded"
            style={{
                background: DS_COLORS.surface.warm,
                border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                borderLeft: `4px solid #D97706`,
            }}
        >
            <div
                style={{
                    fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                    fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                    fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: '#D97706',
                    marginBottom: '6px',
                }}
            >
                {label || 'Concerns raised since approval'}
            </div>
            {flags.map((flag, i) => (
                <div
                    key={i}
                    className="flex items-start gap-2 mb-1"
                    style={{
                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                        fontSize: '14px',
                        color: DS_COLORS.text.primary,
                    }}
                >
                    <Warning weight="bold" style={{ width: '14px', height: '14px', flexShrink: 0, marginTop: '2px', color: '#D97706' }} />
                    <span>{flag.reason || flag.recommended_action || flag.kpi_type}</span>
                </div>
            ))}
        </div>
    );
}

// ─── Principle Card ────────────────────────────────────────────────────────────
function PrincipleCard({
    principle,
    tabId,
    actionLabels,
    reasonOptions,
    columnLabels,
    qualifierLabels,
    statusLabels,
    // Slug -> plain-English maps, both served by GET /api/principle-library/config.
    // Neither is authored here: `moveLabels` is the SAME map the promotion card already
    // reads (review_ui.promotion_card.move_labels) and `scopeLabels` is derived from
    // review_ui.agent_scope_options. One home, two readers — a second copy of either
    // would be duplicate truth, and the copy that drifted would be the one on screen.
    moveLabels,
    scopeLabels,
    onFeedback,
}) {
    const [expanded, setExpanded] = useState(false);
    const [busy, setBusy] = useState(null);
    const [showOverride, setShowOverride] = useState(false);
    const [showPark, setShowPark] = useState(false);

    const isParked = !!principle.is_parked;
    const enforceMode = principle.enforcement_mode || 'observe';
    const modeIntent = enforcementModeIntent(enforceMode);
    // Business-facing lifecycle badge (config-driven). Falls back to the raw mode only
    // during the pre-config load flash.
    const sl = statusLabels || {};
    const statusBadge = tabId === 'pending' ? (sl.pending || 'Awaiting your review')
        : enforceMode === 'capping' ? (sl.capping || 'Active — sets a floor')
            : (sl.trial || 'In trial — informs only');

    // GTC Link-1: all five actions route through handleFeedback.
    // Card-level wrapper (busy state + modal close) → delegates to the page-level
    // handleFeedback (onFeedback prop) which calls submitPrincipleFeedback + reloads.
    // Named distinctly so the GTC-bound chain handler `handleFeedback` is unambiguous.
    const handleCardAction = useCallback(async (id, action, extra = {}) => {
        setBusy(action);
        try {
            await onFeedback(id, action, extra);
        } catch (e) {
            toast.error('Could not save — please try again.');
        } finally {
            setBusy(null);
            setShowOverride(false);
            setShowPark(false);
        }
    }, [onFeedback]);

    return (
        <div
            className="rounded-lg overflow-hidden mb-4"
            style={{
                background: expanded ? DS_COLORS.surface.white : DS_COLORS.surface.warm,
                borderLeft: `4px solid ${expanded ? DS_BORDERS.color : DS_COLORS.border.brand}`,
                borderTop: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                borderRight: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                borderBottom: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                boxShadow: expanded ? DS_COLORS.shadow.card : 'none',
            }}
        >
            {/* Collapsible header */}
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full text-left flex items-center justify-between"
                style={{
                    padding: `0 ${DS_SPACING.cardPadding}px`,
                    minHeight: DS_SPACING.minTouchTarget + 'px',
                    paddingTop: '14px',
                    paddingBottom: '14px',
                }}
            >
                <div className="flex-1 min-w-0">
                    <div
                        className="truncate"
                        style={{
                            fontFamily: DS_TYPOGRAPHY.listItemTitle.fontFamily,
                            fontSize: DS_TYPOGRAPHY.listItemTitle.fontSize,
                            fontWeight: DS_TYPOGRAPHY.listItemTitle.fontWeight,
                            color: DS_COLORS.text.primary,
                        }}
                    >
                        {principle.title || 'Untitled Lens'}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {/* Enforcement mode badge */}
                        <span
                            className={`inline-flex items-center px-2 py-1 rounded-full border ${modeIntent.bg} ${modeIntent.text} ${modeIntent.border}`}
                            style={{ fontSize: '13px', fontWeight: 700 }}
                        >
                            {statusBadge}
                        </span>
                        {/* Parked badge */}
                        {isParked && (
                            <span
                                className={`inline-flex items-center px-2 py-1 rounded-full border ${DESIGN_INTENTS.warning.bg} ${DESIGN_INTENTS.warning.text} ${DESIGN_INTENTS.warning.border}`}
                                style={{ fontSize: '13px', fontWeight: 700 }}
                            >
                                {sl.parked || 'Set aside'}
                            </span>
                        )}
                        {/* Agent scope badge */}
                        {principle.agent_scope && (
                            <span
                                className={`inline-flex items-center px-2 py-1 rounded-full border ${DESIGN_INTENTS.neutral.bg} ${DESIGN_INTENTS.neutral.text} ${DESIGN_INTENTS.neutral.border}`}
                                style={{ fontSize: '12px', fontWeight: 700 }}
                            >
                                {scopeLabels[principle.agent_scope] || principle.agent_scope}
                            </span>
                        )}
                        {/* Decks ridden count */}
                        <span
                            className={`inline-flex items-center px-2 py-1 rounded-full border ${DESIGN_INTENTS.info.bg} ${DESIGN_INTENTS.info.text} ${DESIGN_INTENTS.info.border}`}
                            style={{ fontSize: '12px', fontWeight: 700 }}
                        >
                            {principle.decks_ridden ?? 0} {columnLabels.decks_ridden || 'decks'}
                        </span>
                        {/* Principle strength */}
                        {principle.principle_strength != null && (
                            <span
                                className={`inline-flex items-center px-2 py-1 rounded-full border ${DESIGN_INTENTS.positive.bg} ${DESIGN_INTENTS.positive.text} ${DESIGN_INTENTS.positive.border}`}
                                style={{ fontSize: '12px', fontWeight: 700 }}
                            >
                                {columnLabels.principle_strength || 'Strength'}: {principle.principle_strength}
                            </span>
                        )}
                    </div>
                </div>
                <CaretDown
                    weight="bold"
                    className="ml-4 flex-shrink-0"
                    style={{
                        width: '20px',
                        height: '20px',
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        color: DS_COLORS.text.secondary,
                    }}
                />
            </button>

            {/* Expanded body */}
            {expanded && (
                <div style={{ padding: `0 ${DS_SPACING.cardPadding}px ${DS_SPACING.cardPadding}px` }}>
                    {/* Admission checks (WI-2 / §11.6) — the five qualifier chips + worked example */}
                    <QualifierPanel principle={principle} labels={qualifierLabels || {}} />

                    {/* Principle text */}
                    {principle.principle_text && (
                        <div style={{ marginBottom: DS_SPACING.paragraphSpacing + 'px' }}>
                            <div
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                                    fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    color: DS_COLORS.text.brand,
                                    marginBottom: '6px',
                                }}
                            >
                                {columnLabels.principle_text || 'Lens Statement'}
                            </div>
                            <p
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                    lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                                    color: DS_COLORS.text.primary,
                                    whiteSpace: 'pre-wrap',
                                    margin: 0,
                                }}
                            >
                                {principle.principle_text}
                            </p>
                        </div>
                    )}

                    {/* Analytical move */}
                    {principle.analytical_move && (
                        <div style={{ marginBottom: DS_SPACING.paragraphSpacing + 'px' }}>
                            <div
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                                    fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    color: DS_COLORS.text.brand,
                                    marginBottom: '6px',
                                }}
                            >
                                {columnLabels.analytical_move || 'Analytical Move'}
                            </div>
                            <p
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                    lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                                    color: DS_COLORS.text.primary,
                                    whiteSpace: 'pre-wrap',
                                    margin: 0,
                                }}
                            >
                                {/* The stored value is a controlled-vocabulary slug
                                    (`verify_distribution_claim`). It was printed raw on
                                    every one of the nine cards the CEO reads. The English
                                    for all twelve slugs already existed and was already
                                    being served — this page simply was not reading it.
                                    Falls back to the slug so an unmapped value shows
                                    SOMETHING rather than a blank line. */}
                                {moveLabels[principle.analytical_move] || principle.analytical_move}
                            </p>
                        </div>
                    )}

                    {/* Anti-pattern (if any) */}
                    {principle.anti_pattern && (
                        <div
                            className="mb-4 p-3 rounded"
                            style={{
                                background: DS_COLORS.surface.warm,
                                border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                                borderLeft: `4px solid #DC2626`,
                            }}
                        >
                            <div
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                                    fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    color: '#DC2626',
                                    marginBottom: '4px',
                                }}
                            >
                                {columnLabels.anti_pattern || 'Anti-pattern'}
                            </div>
                            <p
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                    lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                                    color: DS_COLORS.text.primary,
                                    margin: 0,
                                }}
                            >
                                {principle.anti_pattern}
                            </p>
                        </div>
                    )}

                    {/* Trigger / falsifying conditions */}
                    <div className="flex gap-4 mb-4 flex-wrap">
                        {principle.trigger_condition && (
                            <div style={{ flex: '1 1 200px' }}>
                                <div
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                                        fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                                        fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                        color: DS_COLORS.text.secondary,
                                        marginBottom: '4px',
                                    }}
                                >
                                    {columnLabels.trigger_condition || 'Trigger'}
                                </div>
                                <p
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                        fontSize: '14px',
                                        color: DS_COLORS.text.primary,
                                        margin: 0,
                                    }}
                                >
                                    {principle.trigger_condition}
                                </p>
                            </div>
                        )}
                        {principle.falsifying_condition && (
                            <div style={{ flex: '1 1 200px' }}>
                                <div
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                                        fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                                        fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                        color: DS_COLORS.text.secondary,
                                        marginBottom: '4px',
                                    }}
                                >
                                    {columnLabels.falsifying_condition || 'Falsifying condition'}
                                </div>
                                <p
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                        fontSize: '14px',
                                        color: DS_COLORS.text.primary,
                                        margin: 0,
                                    }}
                                >
                                    {principle.falsifying_condition}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Park reason (if parked) */}
                    {isParked && principle.park_reason && (
                        <div
                            className="mb-4 p-3 rounded"
                            style={{
                                background: DS_COLORS.surface.warm,
                                border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                            }}
                        >
                            <div
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                                    fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    color: DS_COLORS.text.secondary,
                                    marginBottom: '4px',
                                }}
                            >
                                {columnLabels.park_reason || 'Why it was set aside'}
                            </div>
                            <p
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                    fontSize: '14px',
                                    color: DS_COLORS.text.primary,
                                    margin: 0,
                                }}
                            >
                                {principle.park_reason}
                            </p>
                        </div>
                    )}

                    {/* Watcher flags */}
                    <WatcherFlags flags={principle.watcher_flags} label={columnLabels.watcher_flags} />

                    {/* The raw `Decision: accepted` line was DELETED rather than relabelled.
                        It printed a bare enum on all nine cards, and it was redundant on its
                        own face: the status badge in the header already says the same thing
                        in English ("In trial — informs only" / "Active — sets a floor"), and
                        a set-aside lens already carries its own badge. Deletion over
                        addition — a plain-language wrapper here would have been a second way
                        of saying what the badge above says better. */}

                    {/* Action bar */}
                    {tabId === 'pending' && (
                        <div
                            className="flex items-center gap-3 mt-6 pt-4 flex-wrap"
                            style={{ borderTop: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}` }}
                        >
                            <button
                                onClick={() => setShowOverride(true)}
                                disabled={!!busy}
                                className={`inline-flex items-center gap-2 px-4 rounded-md border ${INTENT_TO_BUTTON.negative.bg} ${INTENT_TO_BUTTON.negative.text} ${INTENT_TO_BUTTON.negative.border} ${INTENT_TO_BUTTON.negative.hover} disabled:opacity-50`}
                                style={{
                                    minHeight: DS_SPACING.minTouchTarget + 'px',
                                    borderRadius: DS_BORDERS.radiusButton,
                                    fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                                    fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                                }}
                            >
                                {busy === 'override' ? <SpinnerGap className="w-4 h-4 animate-spin" /> : null}
                                {actionLabels.override || 'Reject'}
                            </button>
                            <div className="flex-1" />
                            <button
                                onClick={() => handleCardAction(principle.linked_principle_id || principle.id, 'agree')}
                                disabled={!!busy}
                                className={`inline-flex items-center gap-2 px-5 rounded-md border ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-50`}
                                style={{
                                    minHeight: DS_SPACING.minTouchTarget + 'px',
                                    borderRadius: DS_BORDERS.radiusButton,
                                    fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                                    fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                                }}
                            >
                                {busy === 'agree' ? <SpinnerGap className="w-4 h-4 animate-spin" /> : null}
                                {actionLabels.agree || 'Agree'}
                            </button>
                        </div>
                    )}

                    {/* Accepted tab actions: Promote to capping / Park (or Unpark when parked) */}
                    {tabId === 'accepted' && (
                        <div
                            className="flex items-center gap-3 mt-6 pt-4 flex-wrap"
                            style={{ borderTop: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}` }}
                        >
                            {isParked ? (
                                <button
                                    onClick={() => handleCardAction(principle.linked_principle_id || principle.id, 'unpark')}
                                    disabled={!!busy}
                                    className={`inline-flex items-center gap-2 px-5 rounded-md border ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-50`}
                                    style={{
                                        minHeight: DS_SPACING.minTouchTarget + 'px',
                                        borderRadius: DS_BORDERS.radiusButton,
                                        fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                                        fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                                        fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                                    }}
                                >
                                    {busy === 'unpark' ? <SpinnerGap className="w-4 h-4 animate-spin" /> : null}
                                    {actionLabels.unpark || 'Bring back'}
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setShowPark(true)}
                                        disabled={!!busy}
                                        className={`inline-flex items-center gap-2 px-4 rounded-md border ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} disabled:opacity-50`}
                                        style={{
                                            minHeight: DS_SPACING.minTouchTarget + 'px',
                                            borderRadius: DS_BORDERS.radiusButton,
                                            fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                                            fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                                            fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                                        }}
                                    >
                                        {busy === 'park' ? <SpinnerGap className="w-4 h-4 animate-spin" /> : null}
                                        {actionLabels.park || 'Set aside'}
                                    </button>
                                    <div className="flex-1" />
                                    {enforceMode === 'observe' && (
                                        <button
                                            onClick={() => handleCardAction(principle.linked_principle_id || principle.id, 'promote_to_capping')}
                                            disabled={!!busy}
                                            className={`inline-flex items-center gap-2 px-5 rounded-md border ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-50`}
                                            style={{
                                                minHeight: DS_SPACING.minTouchTarget + 'px',
                                                borderRadius: DS_BORDERS.radiusButton,
                                                fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                                                fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                                                fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                                            }}
                                        >
                                            {busy === 'promote_to_capping' ? <SpinnerGap className="w-4 h-4 animate-spin" /> : null}
                                            {actionLabels.promote_to_capping || 'Set a floor on the grade'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Override reason modal */}
            <ModalSelector
                open={showOverride}
                title={actionLabels.override || 'Reject'}
                options={reasonOptions}
                onSelect={reason => handleCardAction(principle.linked_principle_id || principle.id, 'override', { reason })}
                onClose={() => setShowOverride(false)}
                busy={busy === 'override'}
            />

            {/* Park confirmation modal (uses rejection reasons as park reasons) */}
            <ModalSelector
                open={showPark}
                title={actionLabels.park || 'Set this lens aside'}
                options={reasonOptions}
                onSelect={reason => handleCardAction(principle.linked_principle_id || principle.id, 'park', { reason })}
                onClose={() => setShowPark(false)}
                busy={busy === 'park'}
            />
        </div>
    );
}

// ─── Tab chips ─────────────────────────────────────────────────────────────────
function TabChips({ tabs, activeTab, onTabChange, tabCounts }) {
    return (
        <div className="flex items-center gap-2 mb-6">
            {tabs.map(tab => {
                const isActive = activeTab === tab.id;
                const intent = isActive ? INTENT_TO_BUTTON.primary : INTENT_TO_BUTTON.neutral;
                const count = tabCounts[tab.id] || 0;
                return (
                    <button
                        key={tab.id}
                        onClick={() => onTabChange(tab.id)}
                        className={`inline-flex items-center gap-2 px-5 rounded-full font-semibold transition-colors border ${intent.bg} ${intent.text} ${intent.border} ${!isActive ? intent.hover : ''}`}
                        style={{
                            minHeight: DS_SPACING.minTouchTarget + 'px',
                            fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                            fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                            fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                        }}
                    >
                        {tab.label}
                        {count > 0 && (
                            <span
                                className="inline-flex items-center justify-center px-2 py-0.5 rounded-full"
                                style={{
                                    minWidth: '24px',
                                    background: isActive ? 'rgba(255,255,255,0.2)' : DS_COLORS.surface.warm,
                                    fontSize: '12px',
                                    fontWeight: 700,
                                }}
                            >
                                {count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function PrincipleLibraryAgent() {
    const [activeTab, setActiveTab] = useState('pending');
    const [pending, setPending] = useState([]);
    const [accepted, setAccepted] = useState([]);
    const [rejected, setRejected] = useState([]);
    const [config, setConfig] = useState({
        rejection_reasons: [],
        feedback_rating_scale: {},
        agent_scope_options: [],
        action_labels: {},
        tab_labels: {},
        column_labels: {},
    });
    const [loading, setLoading] = useState(true);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [cfg, p, a, r] = await Promise.allSettled([
                getPrincipleLibraryConfig(),
                getPrincipleLibraryPending(),
                getPrincipleLibraryAccepted(),
                getPrincipleLibraryRejected(),
            ]);
            if (cfg.status === 'fulfilled') setConfig(cfg.value || {});
            if (p.status === 'fulfilled') setPending(p.value?.principles || []);
            if (a.status === 'fulfilled') setAccepted(a.value?.principles || []);
            if (r.status === 'fulfilled') setRejected(r.value?.principles || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    // Build tab list from config.tab_labels (server-driven copy) with TAB_IDS order.
    const tabs = useMemo(() => {
        const labels = config.tab_labels || {};
        return TAB_IDS.map(id => ({
            id,
            label: labels[id] || id.charAt(0).toUpperCase() + id.slice(1),
        }));
    }, [config]);

    // Rejection/park reason options from config.rejection_reasons.
    const reasonOptions = useMemo(() => {
        return (config.rejection_reasons || []).map(r => ({ value: r, label: r }));
    }, [config]);

    const actionLabels = config.action_labels || {};
    const columnLabels = config.column_labels || {};
    const qualifierLabels = config.qualifier_labels || {};
    const statusLabels = config.status_labels || {};
    // The SAME slug -> plain-English map the promotion card already reads. The route
    // serves review_ui.promotion_card whole, so this needed no backend change and no
    // new config: the English was sitting there being served and this page was simply
    // printing the slug instead. Read it, never re-author it.
    const moveLabels = (config.promotion_card || {}).move_labels || {};
    // Derived from the agent_scope_options config already serves to the author form, so
    // the badge on the card and the dropdown that set it can never disagree about what
    // a scope is called.
    const scopeLabels = useMemo(() => Object.fromEntries(
        (config.agent_scope_options || []).map(o => [o.value, o.label]),
    ), [config]);
    // Page-header copy, server-driven (review_ui.page_intro) — the fallback below is
    // held to the same no-internal-vocabulary bar as config
    // (test_principle_promotion_card_plain_language.py's review_ui sweep). Previously
    // this sentence was hardcoded directly in the JSX with no config path at all — the
    // one CEO-facing string on this page config could not override, so it leaked
    // "observed"/"capping" past every prior sweep of this surface.
    // Kept word-for-word in step with review_ui.page_intro. The old wording told the CEO
    // to "override to reject it" — naming a control that does not exist on the screen,
    // since the button has read "Reject" for some time.
    const pageIntro = config.page_intro
        || 'Judgment calls learned from decks you have already reviewed. Approve a lens to put it into trial, reject one you disagree with, or set a floor on the grade once a lens has earned it.';

    const tabCounts = useMemo(() => ({
        pending: pending.length,
        accepted: accepted.length,
        rejected: rejected.length,
    }), [pending, accepted, rejected]);

    // GTC Link-1: handler_name="handleFeedback" — all card actions route through here.
    //
    // THE RELOAD RUNS IN `finally`, ON EVERY OUTCOME. That placement is the fix, not a
    // tidy-up. Reproduced on the deployed surface rather than reasoned about: the
    // set-aside write COMMITS on the server and the response then fails to arrive (the
    // scheduler sweep runs inside the request path — TA's PR #1595). With the reload
    // sitting on the success path only, the catch swallowed the failure and the card
    // went on rendering pre-write state, so the control read as completely dead while
    // the change had in fact been saved. The CEO then presses it again against a screen
    // that is already wrong.
    //
    // The rule this encodes: a screen that disagrees with the server is never the
    // resting state. When we cannot confirm an outcome we go and re-read it, rather
    // than assert on the CEO's behalf that nothing happened.
    const handleFeedback = useCallback(async (id, action, extra = {}) => {
        try {
            await submitPrincipleFeedback(id, action, extra);
            toast.success('Saved.');
        } catch (e) {
            // Deliberately NOT "could not save" — the write may well have landed, and
            // saying otherwise is the lie that made this bug invisible. Claim only what
            // is actually known: that the outcome could not be confirmed. The reload
            // below then shows whichever state the server really holds.
            toast.error('Could not confirm that — showing the latest saved state.');
        } finally {
            await loadAll();
        }
    }, [loadAll]);

    const list =
        activeTab === 'accepted' ? accepted :
        activeTab === 'rejected' ? rejected : pending;

    const renderBody = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center py-16" style={{ color: DS_COLORS.text.secondary }}>
                    <SpinnerGap className="w-6 h-6 animate-spin mr-3" /> Loading lenses…
                </div>
            );
        }
        if (list.length === 0) {
            return (
                <div
                    className="text-center py-16"
                    style={{
                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                        color: DS_COLORS.text.secondary,
                    }}
                >
                    No lenses in this tab yet.
                </div>
            );
        }
        return list.map(p => (
            <PrincipleCard
                key={p.id}
                principle={p}
                tabId={activeTab}
                actionLabels={actionLabels}
                reasonOptions={reasonOptions}
                columnLabels={columnLabels}
                qualifierLabels={qualifierLabels}
                statusLabels={statusLabels}
                moveLabels={moveLabels}
                scopeLabels={scopeLabels}
                onFeedback={handleFeedback}
            />
        ));
    };

    return (
        <div>
            <div className="mb-6">
                <h1
                    style={{
                        fontFamily: DS_TYPOGRAPHY.pageTitle.fontFamily,
                        fontSize: DS_TYPOGRAPHY.pageTitle.fontSize,
                        fontWeight: DS_TYPOGRAPHY.pageTitle.fontWeight,
                        letterSpacing: DS_TYPOGRAPHY.pageTitle.letterSpacing,
                        color: DS_COLORS.text.brand,
                        marginBottom: '8px',
                    }}
                >
                    Principle Library
                </h1>
                <p
                    style={{
                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                        color: DS_COLORS.text.secondary,
                    }}
                >
                    {pageIntro}
                </p>
            </div>
            <TabChips
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                tabCounts={tabCounts}
            />
            {renderBody()}
        </div>
    );
}
